import { useState } from "react";
import {
  Box,
  SimpleGrid,
  Modal,
  ScaleFade,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
} from "@chakra-ui/react";
import Image from "components/base/Image";
import Container from "components/base/Container";
import MainContainer from "components/sections/MainContainer";
import TextHeader from "components/sections/TextHeader";
import Text from "components/base/Text";
import MetaHead from "components/base/MetaHead";
import Trans from "components/base/Trans";
import { QuoteSection } from "components/sections/TestimonialsSection";
import ImageSection from "components/sections/ImageSection";
import LinkColumnsSection from "components/sections/LinkColumnsSection";
import TextImageColumns2 from "components/sections/TextImageColumns2";
import HorizontalSlideSection from "components/sections/HorizontalSlideSection";
import { getTeams } from "lib/payloadApi";
import TeamVideoSection from "components/sections/TeamVideoSection";
import TeamMemberCard from "components/blocks/TeamMemberCard";
import DarkQuoteSection from "components/sections/DarkQuoteSection";
import { MotionBox, fadeIn } from "utils/motion";

const aboutSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://www.hillhelicopters.com/#organization",
      name: "Hill Helicopters",
      url: "https://www.hillhelicopters.com",
      founder: { "@id": "https://www.hillhelicopters.com/#jason-hill" },
    },
    {
      "@type": "Person",
      "@id": "https://www.hillhelicopters.com/#jason-hill",
      name: "Jason Hill",
      jobTitle: "Founder & CEO",
      worksFor: { "@id": "https://www.hillhelicopters.com/#organization" },
      url: "https://www.hillhelicopters.com/about-us",
    },
  ],
});

const AnimationBox = ({ children }) => (
  <MotionBox
    variants={fadeIn}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.2 }}
  >
    {children}
  </MotionBox>
);

const TeamModal = ({ isOpen, onClose, member }) => (
  <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
    <ModalOverlay />
    <ModalContent bg="transparent" m={[4, 8, 8, 0]} maxHeight="80vh">
      <ScaleFade initialScale={0.6} in={isOpen}>
        <ModalCloseButton color="white" />
        <ModalBody display="flex" p={0} bg="black.700">
          <Box display={["none", null, "block"]} w="50%">
            <Image
              src={member.image.url}
              alt={member.name}
              height="100%"
              objectFit="cover"
            />
          </Box>
          <Box p={6} flex="1">
            <Text
              type="p_xl"
              fontWeight="bold"
              lineHeight={1.5}
              color="white"
              py={8}
            >
              {member.name}
            </Text>
            <Text type="title_sm" color="primary" pb={2}>
              {member.title}
            </Text>
            <Text type="p_md" color="white">
              {member.description}
            </Text>
          </Box>
        </ModalBody>
      </ScaleFade>
    </ModalContent>
  </Modal>
);

export default function Team({ teams }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedMember, setSelectedMember] = useState({
    section: 0,
    index: 0,
  });

  const handleCardClick = (section, index) => {
    setSelectedMember({ section, index });
    onOpen();
  };

  return (
    <MainContainer navStyle={{ gradient: false, inverted: false }}>
      <MetaHead
        title="LBL_TEAM_META_TITLE"
        description="LBL_TEAM_META_DESC"
        imageUrl="/images/meta/team.jpg"
        structuredData={aboutSchema}
      />
      <TextHeader
        page={<Trans id="LBL_TEAM_PAGE" />}
        title={<Trans id="LBL_TEAM_TITLE" />}
        inverted
      />
      <TeamVideoSection />
      <QuoteSection
        bg="black.900"
        quote={<Trans id="LBL_TEAM_QUOTE" />}
        author={<Trans id="LBL_TEAM_QUOTE_AUTHOR" />}
        title={<Trans id="LBL_TEAM_QUOTE_TITLE" />}
      />
      <HorizontalSlideSection
        bg="black.900"
        baseSrc="/images/team/gallery/"
        items={[
          "01-vert",
          "02",
          "03-vert",
          "04",
          "05",
          "06-vert",
          "07-vert",
          "10",
          "11",
          "12",
          "08-vert",
          "09-vert",
        ]}
      />
      <AnimationBox>
        <Container pb={[10, null, 14]}>
          <Text
            type="headline_md"
            color="white"
            maxW={["100%", null, "50%"]}
            pb={[8, null, 10]}
          >
            <Trans id="LBL_TEAM_FORGING_TITLE" />
          </Text>
          <Text type="p_lg" color="white" maxW={["100%", null, "60%"]}>
            <Trans id="LBL_TEAM_FORGING_DESCRIPTION" />
          </Text>
        </Container>
      </AnimationBox>
      <ImageSection image="/images/team/forging.jpg" bg="black.700" />
      <AnimationBox>
        <Container pb={[0]}>
          <TextHeader
            page={<Trans id="LBL_TEAM_CORE_TITLE" />}
            title={<Trans id="LBL_TEAM_CORE_SUBTITLE" />}
            inverted
            mx={0}
            containerProps={{
              pt: 0,
              pb: [2],
            }}
          />
        </Container>
      </AnimationBox>
      <TextImageColumns2
        isInverted
        bg="black.900"
        topTitle={<Trans id="LBL_TEAM_CHAIRMAN_NAME" />}
        topSubtitle={<Trans id="LBL_TEAM_CHAIRMAN_TITLE" />}
        topDescription={<Trans id="LBL_TEAM_CHAIRMAN_DESCRIPTION_1" />}
        topImage="/images/team/jason_01.jpg"
        bottomDescription={<Trans id="LBL_TEAM_CHAIRMAN_DESCRIPTION_2" />}
        bottomImage="/images/team/jason_02.jpg"
      />
      {teams.map((team, section) => (
        <Container key={team.id} pt={section === 0 ? [4, null, 8] : [0]}>
          <AnimationBox>
            {section !== 0 && (
              <TextHeader
                page={<Trans id="LBL_TEAM_CORE_TITLE" />}
                title={team.data.category_name}
                inverted
                mx={0}
                containerProps={{
                  pt: 0,
                  pb: [2],
                }}
              />
            )}
            <SimpleGrid
              columns={section === 0 ? [1, 2, 3] : [1, 2, 3, 4]}
              spacing={8}
              mb={8}
            >
              {team.data.team_element.map((member, index) => (
                <TeamMemberCard
                  key={member.id}
                  order={section}
                  {...member}
                  onClick={() => handleCardClick(section, index)}
                />
              ))}
            </SimpleGrid>
          </AnimationBox>
        </Container>
      ))}
      <LinkColumnsSection
        isInverted
        bg="gray.800"
        title={<Trans id="LBL_TEAM_CAREERS_TITLE" />}
        description={<Trans id="LBL_TEAM_CAREERS_DESCRIPTION" />}
        image="/images/team/careers.png"
        ctaText={<Trans id="LBL_TEAM_CAREERS_CTA" />}
        cta="/careers"
      />
      <ImageSection image="/images/team/footer-team.jpg" bg="black.700" />
      <DarkQuoteSection
        title={<Trans id="LBL_TEAM_FOOTER_TITLE" />}
        description={<Trans id="LBL_TEAM_FOOTER_DESCRIPTION" />}
      />
      <TeamModal
        isOpen={isOpen}
        onClose={onClose}
        member={
          teams[selectedMember.section].data.team_element[selectedMember.index]
        }
      />
    </MainContainer>
  );
}

const TEAM_ORDER = ["senior-management", "engineering"];

export async function getServerSideProps({ locale }) {
  const teams = (await getTeams(locale)).results.sort(
    (a, b) => TEAM_ORDER.indexOf(a.slug) - TEAM_ORDER.indexOf(b.slug)
  );

  return {
    props: { teams },
  };
}
